import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { UserCircle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { UserDetailsModal } from './UserDetailsModal';

interface UserData {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  role: string;
  subscription_status: string | null;
  subscription_plan: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  activity_count?: number;
}

export function UsersManagement() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      const { data, error } = await supabase.rpc('get_all_users_for_admin');

      if (error) throw error;

      const usersWithActivity = await Promise.all(
        (data || []).map(async (user: any) => {
          const { data: activityData } = await supabase
            .rpc('get_user_activity_count', { p_user_id: user.id });

          const totalActivity = activityData?.[0]
            ? Number(activityData[0].transactions_count || 0) +
              Number(activityData[0].budget_lines_count || 0) +
              Number(activityData[0].scenarios_count || 0)
            : 0;

          return {
            ...user,
            activity_count: totalActivity,
          };
        })
      );

      setUsers(usersWithActivity);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }

  // Function reserved for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function updateSubscriptionStatus(userId: string, status: 'active' | 'suspended' | 'cancelled') {
    try {
      const { data: existingSub } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (existingSub) {
        await supabase
          .from('subscriptions')
          .update({ status })
          .eq('user_id', userId);
      } else {
        await supabase.from('subscriptions').insert({
          user_id: userId,
          status,
          plan_type: 'monthly',
          price: 29.99,
        });
      }

      await fetchUsers();
    } catch (error) {
      console.error('Error updating subscription:', error);
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Chargement des utilisateurs...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des utilisateurs</h2>
        <div className="text-sm text-gray-600">{users.length} utilisateur(s)</div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Société</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Abonnement</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inscription</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activité</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <UserCircle className="text-gray-400" size={20} />
                    <div>
                      <div className="font-medium text-gray-900">
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : 'Sans nom'}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600">
                  {user.company || '-'}
                </td>
                <td className="px-4 py-4">
                  <div className="space-y-1">
                    {user.subscription_status ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              user.subscription_status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : user.subscription_status === 'suspended'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {user.subscription_status === 'active' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                            {user.subscription_status}
                          </span>
                          {user.subscription_plan && (
                            <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">
                              {user.subscription_plan}
                            </span>
                          )}
                        </div>
                        {user.stripe_customer_id && (
                          <div className="text-xs text-gray-500">
                            Client: <span className="font-mono text-gray-700">{user.stripe_customer_id}</span>
                          </div>
                        )}
                        {user.stripe_subscription_id && (
                          <div className="text-xs text-gray-500">
                            Sub: <span className="font-mono text-gray-700">{user.stripe_subscription_id}</span>
                          </div>
                        )}
                        {user.current_period_end && user.subscription_status === 'active' && (
                          <div className="text-xs text-gray-600">
                            Renouvellement: {new Date(user.current_period_end).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                        )}
                      </>
                    ) : user.trial_ends_at ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Clock size={12} />
                        Essai {new Date(user.trial_ends_at) > new Date() ? 'actif' : 'expiré'}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">Aucun</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600">
                  {new Date(user.created_at).toLocaleDateString('fr-FR')}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-600">{user.activity_count} actions</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <button
                    onClick={() => setSelectedUserId(user.id)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Voir détails
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedUserId && (
        <UserDetailsModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onUpdate={fetchUsers}
        />
      )}
    </div>
  );
}
